/**
 * COURSE DETAIL PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { Course } from '../types/course';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Panel, Button, Badge } from '../design/primitives';
import { Spinner, Alert } from '../components/ui';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  PlusIcon,
  PlayCircleIcon,
  ChevronDownIcon,
  CloudArrowUpIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import CourseForm from '../components/forms/CourseForm';
import ModuleForm from '../components/forms/ModuleForm';
import LessonForm from '../components/forms/LessonForm';
import EditModuleForm from '../components/forms/EditModuleForm';
import EditLessonForm from '../components/forms/EditLessonForm';

const CourseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isModuleFormOpen, setIsModuleFormOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [isLessonFormOpen, setIsLessonFormOpen] = useState(false);
  const [isEditModuleFormOpen, setIsEditModuleFormOpen] = useState(false);
  const [isEditLessonFormOpen, setIsEditLessonFormOpen] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null);
  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set());
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [bulkUploadModuleId, setBulkUploadModuleId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [isDownloadingTranscripts, setIsDownloadingTranscripts] = useState(false);

  const fetchCourse = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/courses/${id}/`);
      setCourse(response.data);

      // Expand all modules by default for better UX
      const moduleIds = new Set<number>(response.data.modules.map((m: any) => m.id as number));
      setExpandedModules(moduleIds);
    } catch (error) {
      console.error('Error fetching course:', error);
      setError('Failed to load course details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCourse();
  }, [id, fetchCourse]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this course?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/courses/${id}/`);
      navigate('/learning');
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course');
    }
  };

  const handleDownloadTranscripts = async () => {
    try {
      setIsDownloadingTranscripts(true);
      const response = await apiClient.get(`/api/courses/${id}/download_transcripts/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename="')[1]?.replace('"', '') || 'transcripts.zip'
        : 'transcripts.zip';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error.response?.status === 404) {
        alert('No transcripts found for this course.');
      } else {
        console.error('Error downloading transcripts:', error);
        alert('Failed to download transcripts');
      }
    } finally {
      setIsDownloadingTranscripts(false);
    }
  };

  const handleModuleDelete = async (moduleId: number) => {
    if (!window.confirm('Are you sure you want to delete this module? This will also delete all lessons in this module.')) {
      return;
    }

    try {
      await apiClient.delete(`/api/modules/${moduleId}/`);
      await fetchCourse();
    } catch (error) {
      console.error('Error deleting module:', error);
      alert('Failed to delete module');
    }
  };

  const handleLessonDelete = async (lessonId: number) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/lessons/${lessonId}/`);
      await fetchCourse();
    } catch (error) {
      console.error('Error deleting lesson:', error);
      alert('Failed to delete lesson');
    }
  };

  const handleModuleCompletion = async (moduleId: number, isCompleted: boolean) => {
    try {
      await apiClient.patch(`/api/modules/${moduleId}/`, { is_completed: isCompleted });
      await fetchCourse();
    } catch (error) {
      console.error('Error updating module completion:', error);
      alert('Failed to update module completion status');
    }
  };

  const handleLessonCompletion = async (lessonId: number, isCompleted: boolean) => {
    try {
      await apiClient.patch(`/api/lessons/${lessonId}/`, { is_completed: isCompleted });
      await fetchCourse();
    } catch (error) {
      console.error('Error updating lesson completion:', error);
      alert('Failed to update lesson completion status');
    }
  };

  const handleAddLesson = (moduleId: number) => {
    setSelectedModuleId(moduleId);
    setIsLessonFormOpen(true);
  };

  const handleEditModule = (moduleId: number) => {
    setSelectedModuleId(moduleId);
    setIsEditModuleFormOpen(true);
  };

  const handleEditLesson = (lessonId: number) => {
    setSelectedLessonId(lessonId);
    setIsEditLessonFormOpen(true);
  };

  const toggleModule = (moduleId: number) => {
    setExpandedModules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  const handleBulkUpload = (moduleId: number) => {
    setBulkUploadModuleId(moduleId);
    setIsBulkUploadOpen(true);
  };

  const handleBulkUploadSubmit = async (files: FileList) => {
    if (!bulkUploadModuleId || files.length === 0) return;

    // Check file count limit
    const MAX_FILES = 100;
    if (files.length > MAX_FILES) {
      alert(`Too many files! Maximum ${MAX_FILES} videos per batch. You selected ${files.length}.\n\nPlease select fewer files.`);
      return;
    }

    setIsUploading(true);
    setUploadStatus('Creating lessons...');
    setUploadProgress({});
    setTotalFiles(files.length);

    try {
      // Step 1: Generate pre-signed S3 URLs for each file
      const filesData = Array.from(files).map((file) => ({
        filename: file.name,
        content_type: file.type || 'video/mp4',
      }));

      const urlResponse = await apiClient.post(`/api/modules/${bulkUploadModuleId}/generate_upload_urls/`, {
        files: filesData,
      });

      const { uploads, errors } = urlResponse.data;

      if (errors && errors.length > 0) {
        console.error('Errors generating URLs:', errors);
      }

      if (!uploads || uploads.length === 0) {
        alert('Failed to generate upload URLs. Please try again.');
        setIsUploading(false);
        return;
      }

      // Step 2: Upload each file directly to S3
      setUploadStatus(`Uploading ${uploads.length} videos to S3...`);

      const uploadPromises = uploads.map(async (uploadInfo: any, index: number) => {
        const file = files[index];
        const fileKey = uploadInfo.filename;

        try {
          // Upload directly to S3 using pre-signed URL
          await fetch(uploadInfo.upload_url, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': uploadInfo.content_type || file.type,
            },
          });

          // Update progress
          setUploadProgress((prev) => ({ ...prev, [fileKey]: 100 }));

          // Step 3: Mark upload as complete on backend
          await apiClient.post(`/api/lessons/${uploadInfo.lesson_id}/complete_upload/`);

          return { success: true, filename: fileKey };
        } catch (error) {
          console.error(`Error uploading ${fileKey}:`, error);
          return { success: false, filename: fileKey, error };
        }
      });

      const results = await Promise.all(uploadPromises);
      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      if (failCount > 0) {
        alert(`Upload complete!\n\nSuccessful: ${successCount}\nFailed: ${failCount}\n\nFailed files may need to be re-uploaded.`);
      } else {
        alert(`Successfully uploaded ${successCount} videos!`);
      }

      setIsBulkUploadOpen(false);
      setBulkUploadModuleId(null);
      setUploadProgress({});
      setUploadStatus('');
      await fetchCourse();
    } catch (error: any) {
      console.error('Error in bulk upload:', error);
      alert(`Failed to upload videos: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'accent' | 'warning' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'accent';
      case 'paused':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatStatusText = (status: string): string => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !course) {
    return <Alert variant="error">{error || 'Course not found'}</Alert>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
          <Button variant="secondary" onClick={() => navigate('/learning')} icon={<ArrowLeftIcon style={{ width: '16px', height: '16px' }} />}>
            Back
          </Button>
          <h1
            style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h1,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0,
            }}
          >
            {course.title}
          </h1>
          <Badge color={getStatusColor(course.status)}>{formatStatusText(course.status)}</Badge>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          <Button variant="secondary" onClick={handleDownloadTranscripts} disabled={isDownloadingTranscripts} icon={<ArrowDownTrayIcon style={{ width: '16px', height: '16px' }} />}>
            {isDownloadingTranscripts ? 'Downloading...' : 'Download Transcripts'}
          </Button>
          <Button variant="secondary" onClick={() => setIsEditModalOpen(true)} icon={<PencilIcon style={{ width: '16px', height: '16px' }} />}>
            Edit
          </Button>
          <Button variant="danger" onClick={handleDelete} icon={<TrashIcon style={{ width: '16px', height: '16px' }} />}>
            Delete
          </Button>
        </div>
      </div>

      {/* Course Info */}
      <Panel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          <h2
            style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h2,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0,
            }}
          >
            Course Information
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: SPACING[16] }}>
            <div>
              <dt
                style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[4],
                }}
              >
                Source
              </dt>
              <dd
                style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.primary,
                  margin: 0,
                }}
              >
                {course.source}
              </dd>
            </div>
            <div>
              <dt
                style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[4],
                }}
              >
                Started
              </dt>
              <dd
                style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.primary,
                  margin: 0,
                }}
              >
                {course.started_at || 'Not started'}
              </dd>
            </div>
          </div>
          <div>
            <dt
              style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '500',
                color: COLORS.text.secondary,
                marginBottom: SPACING[4],
              }}
            >
              Description
            </dt>
            <dd
              style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.primary,
                margin: 0,
              }}
            >
              {course.description || 'No description provided'}
            </dd>
          </div>

          {/* Modules Section */}
          <div style={{ marginTop: SPACING[24] }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[16] }}>
              <h2
                style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h2,
                  fontWeight: TYPOGRAPHY.headings.weight,
                  color: COLORS.text.primary,
                  margin: 0,
                }}
              >
                Modules
              </h2>
              <Button variant="secondary" onClick={() => setIsModuleFormOpen(true)} icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}>
                Add Module
              </Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
              {course.modules.map((module) => (
                <Panel key={module.id} noPadding>
                  {/* Module Header */}
                  <button
                    onClick={() => toggleModule(module.id)}
                    style={{
                      width: '100%',
                      backgroundColor: COLORS.bg.hover,
                      padding: SPACING[12],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.1s ease',
                      borderTopLeftRadius: RADIUS,
                      borderTopRightRadius: RADIUS,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.base)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleModuleCompletion(module.id, !module.is_completed);
                        }}
                        style={{
                          padding: SPACING[4],
                          borderRadius: '50%',
                          border: 'none',
                          backgroundColor: 'transparent',
                          cursor: 'pointer',
                          color: module.is_completed ? COLORS.status.success : COLORS.text.tertiary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'color 0.1s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = module.is_completed ? COLORS.status.success : COLORS.text.secondary)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = module.is_completed ? COLORS.status.success : COLORS.text.tertiary)}
                      >
                        <CheckCircleIcon style={{ width: '24px', height: '24px' }} />
                      </button>
                      <h3
                        style={{
                          fontFamily: TYPOGRAPHY.headings.fontFamily,
                          fontSize: TYPOGRAPHY.headings.sizes.h3,
                          fontWeight: TYPOGRAPHY.headings.weight,
                          color: COLORS.text.primary,
                          margin: 0,
                        }}
                      >
                        {module.title}
                      </h3>
                      <ChevronDownIcon
                        style={{
                          width: '20px',
                          height: '20px',
                          color: COLORS.text.tertiary,
                          transition: 'transform 0.2s ease',
                          transform: expandedModules.has(module.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                      <Button
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddLesson(module.id);
                        }}
                        icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}
                      >
                        Add Lesson
                      </Button>
                      <Button
                        variant="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBulkUpload(module.id);
                        }}
                        icon={<CloudArrowUpIcon style={{ width: '16px', height: '16px' }} />}
                      >
                        Bulk Upload
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditModule(module.id);
                        }}
                        icon={<PencilIcon style={{ width: '16px', height: '16px' }} />}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleModuleDelete(module.id);
                        }}
                        icon={<TrashIcon style={{ width: '16px', height: '16px' }} />}
                      >
                        Delete
                      </Button>
                    </div>
                  </button>

                  {/* Module Lessons */}
                  <div style={{ display: expandedModules.has(module.id) ? 'block' : 'none' }}>
                    {module.lessons.length === 0 ? (
                      <div
                        style={{
                          padding: SPACING[24],
                          textAlign: 'center',
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.default,
                          color: COLORS.text.secondary,
                        }}
                      >
                        No lessons yet. Click "Add Lesson" to create one.
                      </div>
                    ) : (
                      module.lessons.map((lesson, index) => (
                        <div
                          key={lesson.id}
                          onClick={() => navigate(`/lessons/${lesson.id}`)}
                          style={{
                            padding: SPACING[16],
                            cursor: 'pointer',
                            transition: 'background-color 0.1s ease',
                            borderTop: index === 0 ? `1px solid ${COLORS.border.default}` : 'none',
                            borderBottom: `1px solid ${COLORS.border.default}`,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: SPACING[12], flex: 1 }}>
                              {/* Completion checkbox */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLessonCompletion(lesson.id, !lesson.is_completed);
                                }}
                                style={{
                                  marginTop: SPACING[4],
                                  padding: SPACING[4],
                                  borderRadius: '50%',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  cursor: 'pointer',
                                  color: lesson.is_completed ? COLORS.status.success : COLORS.text.tertiary,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  transition: 'color 0.1s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = lesson.is_completed ? COLORS.status.success : COLORS.text.secondary;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = lesson.is_completed ? COLORS.status.success : COLORS.text.tertiary;
                                }}
                              >
                                <CheckCircleIcon style={{ width: '20px', height: '20px' }} />
                              </button>

                              {/* Lesson info */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Lesson title */}
                                <h4
                                  style={{
                                    fontFamily: TYPOGRAPHY.headings.fontFamily,
                                    fontSize: TYPOGRAPHY.body.sizes.default,
                                    fontWeight: '500',
                                    color: COLORS.text.primary,
                                    margin: `0 0 ${SPACING[8]} 0`,
                                    transition: 'color 0.1s ease',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.accent.default)}
                                  onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.text.primary)}
                                >
                                  {lesson.title}
                                </h4>

                                {/* Status indicators */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: SPACING[8], marginBottom: SPACING[8] }}>
                                  {/* Video status */}
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: SPACING[4] }}>
                                    <VideoCameraIcon style={{ width: '14px', height: '14px' }} />
                                    <Badge color={lesson.video_url || lesson.video_file ? 'accent' : 'default'}>
                                      {lesson.video_url || lesson.video_file ? 'Video' : 'No Video'}
                                    </Badge>
                                  </div>

                                  {/* Transcript status */}
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: SPACING[4] }}>
                                    <DocumentTextIcon style={{ width: '14px', height: '14px' }} />
                                    <Badge color={lesson.transcript && lesson.transcript.trim() ? 'success' : 'default'}>
                                      {lesson.transcript && lesson.transcript.trim() ? 'Transcript' : 'No Transcript'}
                                    </Badge>
                                  </div>

                                  {/* Summary status */}
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: SPACING[4] }}>
                                    <ChatBubbleLeftRightIcon style={{ width: '14px', height: '14px' }} />
                                    <Badge color={lesson.summary && lesson.summary.trim() ? 'success' : 'default'}>
                                      {lesson.summary && lesson.summary.trim() ? 'Summary' : 'No Summary'}
                                    </Badge>
                                  </div>

                                  {/* Completion status */}
                                  {lesson.is_completed && (
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: SPACING[4] }}>
                                      <CheckCircleIcon style={{ width: '14px', height: '14px' }} />
                                      <Badge color="success">Completed</Badge>
                                    </div>
                                  )}
                                </div>

                                {/* Action buttons */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditLesson(lesson.id);
                                    }}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: SPACING[4],
                                      padding: `${SPACING[4]} ${SPACING[8]}`,
                                      fontFamily: TYPOGRAPHY.body.fontFamily,
                                      fontSize: TYPOGRAPHY.body.sizes.small,
                                      color: COLORS.text.secondary,
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      borderRadius: RADIUS,
                                      cursor: 'pointer',
                                      transition: 'all 0.1s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.color = COLORS.text.primary;
                                      e.currentTarget.style.backgroundColor = COLORS.bg.hover;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = COLORS.text.secondary;
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <PencilIcon style={{ width: '14px', height: '14px' }} />
                                    Edit
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLessonDelete(lesson.id);
                                    }}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: SPACING[4],
                                      padding: `${SPACING[4]} ${SPACING[8]}`,
                                      fontFamily: TYPOGRAPHY.body.fontFamily,
                                      fontSize: TYPOGRAPHY.body.sizes.small,
                                      color: COLORS.status.error,
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      borderRadius: RADIUS,
                                      cursor: 'pointer',
                                      transition: 'all 0.1s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <TrashIcon style={{ width: '14px', height: '14px' }} />
                                    Delete
                                  </button>
                                  {lesson.video_url && (
                                    <a
                                      href={lesson.video_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: SPACING[4],
                                        padding: `${SPACING[4]} ${SPACING[8]}`,
                                        fontFamily: TYPOGRAPHY.body.fontFamily,
                                        fontSize: TYPOGRAPHY.body.sizes.small,
                                        color: COLORS.accent.default,
                                        backgroundColor: 'transparent',
                                        textDecoration: 'none',
                                        borderRadius: RADIUS,
                                        transition: 'all 0.1s ease',
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }}
                                    >
                                      <PlayCircleIcon style={{ width: '14px', height: '14px' }} />
                                      Watch
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Right-side chevron to indicate clickable */}
                            <div style={{ marginLeft: SPACING[16], flexShrink: 0 }}>
                              <svg
                                style={{ width: '20px', height: '20px', color: COLORS.text.tertiary }}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Panel>
              ))}
            </div>
          </div>
        </div>
      </Panel>

      {/* Modals */}
      <CourseForm
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onCourseCreated={fetchCourse}
        initialCourse={course}
        isEditMode={true}
      />

      <ModuleForm isOpen={isModuleFormOpen} onClose={() => setIsModuleFormOpen(false)} onModuleCreated={fetchCourse} courseId={course?.id || 0} />

      <LessonForm isOpen={isLessonFormOpen} onClose={() => setIsLessonFormOpen(false)} onLessonCreated={fetchCourse} moduleId={selectedModuleId || 0} />

      <EditModuleForm
        isOpen={isEditModuleFormOpen}
        onClose={() => setIsEditModuleFormOpen(false)}
        onModuleUpdated={fetchCourse}
        moduleId={selectedModuleId || 0}
      />

      <EditLessonForm
        isOpen={isEditLessonFormOpen}
        onClose={() => setIsEditLessonFormOpen(false)}
        onLessonUpdated={fetchCourse}
        lessonId={selectedLessonId || 0}
      />

      {/* Bulk Upload to S3 Modal */}
      {isBulkUploadOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: SPACING[24],
          }}
          onClick={() => {
            if (!isUploading) {
              setIsBulkUploadOpen(false);
              setBulkUploadModuleId(null);
            }
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              padding: SPACING[24],
              width: '100%',
              maxWidth: '500px',
            }}
          >
            <h2
              style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: `0 0 ${SPACING[16]} 0`,
              }}
            >
              Bulk Upload Videos
            </h2>
            <p
              style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                color: COLORS.text.secondary,
                margin: `0 0 ${SPACING[8]} 0`,
              }}
            >
              Select multiple video files to upload. Videos will be uploaded directly to S3 storage.
            </p>
            <div
              style={{
                marginBottom: SPACING[16],
                padding: SPACING[12],
                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                border: `1px solid ${COLORS.status.success}`,
                borderRadius: RADIUS,
              }}
            >
              <p
                style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  color: COLORS.status.success,
                  margin: `0 0 ${SPACING[4]} 0`,
                }}
              >
                <strong>Direct Upload:</strong> Videos uploaded directly to S3 (no server memory issues)
              </p>
              <p
                style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  color: COLORS.status.success,
                  margin: 0,
                }}
              >
                <strong>Limit:</strong> Maximum 100 videos per batch
              </p>
            </div>
            <input
              type="file"
              multiple
              accept="video/*"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleBulkUploadSubmit(e.target.files);
                }
              }}
              disabled={isUploading}
              style={{
                width: '100%',
                padding: SPACING[8],
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.primary,
                backgroundColor: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                cursor: isUploading ? 'not-allowed' : 'pointer',
                opacity: isUploading ? 0.5 : 1,
              }}
            />
            {isUploading && (
              <div style={{ marginTop: SPACING[16] }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: SPACING[8] }}>
                  <Spinner size="md" />
                  <span
                    style={{
                      marginLeft: SPACING[8],
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.text.secondary,
                    }}
                  >
                    {uploadStatus}
                  </span>
                </div>
                {Object.keys(uploadProgress).length > 0 && totalFiles > 0 && (
                  <div
                    style={{
                      marginTop: SPACING[8],
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.text.secondary,
                      textAlign: 'center',
                    }}
                  >
                    {Object.keys(uploadProgress).length} of {totalFiles} files uploaded...
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING[8], marginTop: SPACING[24] }}>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsBulkUploadOpen(false);
                  setBulkUploadModuleId(null);
                }}
                disabled={isUploading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetailPage;
